import Dynamic from '../dynamic/Dynamic'
import { domReady } from '../helpers/DOM'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'
import type SlotFactory from '../slots/SlotFactory'
import { RunInContentAds } from '../enums/SiteOptions'
import { Device } from '../helpers'

const densityMap = {
  default: { mobile: 0.28, desktop: 0.25, buffer: 2 },
  medium: { mobile: 0.24, desktop: 0.2, buffer: 2 },
  low: { mobile: 0.2, desktop: 0.15, buffer: 3 },
  none: { mobile: 0.0, desktop: 0.0, buffer: 0 }
}

export default class DynamicRecipeSlots {
  private readonly SLOT_CLASS: SlotClass = 'Recipe'
  private readonly startingParagraphNumber = 0
  private readonly endingParagraphNumber = 0
  private readonly contentRequireText = RunInContentAds.anywhere
  private readonly minParagraphBuffer
  // eslint-disable-next-line no-magic-numbers
  private readonly density
  private readonly target =
    '.wprm-recipe-instructions-container, .tasty-recipes-instructions, .mv-create-instructions-v2'
  private readonly divBuster = true
  private readonly listBuster = true

  constructor(
    readonly model: WebModel,
    private readonly slotFactory: SlotFactory
  ) {
    const instructionsSettings =
      densityMap[model.recipe_instruction_density || 'default']

    this.density = Device.isMobileOrTablet
      ? instructionsSettings.mobile
      : instructionsSettings.desktop
    this.minParagraphBuffer = instructionsSettings.buffer

    domReady(() => {
      this.render()
    })
  }

  render(): void {
    if (
      !this.model.dynamic_recipe_slots ||
      this.model.recipe_instruction_density === 'none'
    ) {
      return
    }

    const recipeElements = Array.from(
      document.querySelectorAll(`${this.target}`)
    )
    recipeElements.forEach((el) => this.dynamicallyCreateSlots(el))
  }

  private dynamicallyCreateSlots(
    target: HTMLElement | Element | null
  ): boolean {
    if (!target || !(target instanceof Element)) {
      Logger.debug(
        `Mediavine: Must provide a valid element to insert ads into.`,
        target
      )

      return false
    }

    if (target.getAttribute('data-recipe-ads-inserted') === 'true') {
      // Don't want to double-insert ads into an element
      // Logger.debug(
      //   `Mediavine: Can't insert ads into the same element twice.`,
      //   target
      // )

      return false
    }
    target.setAttribute('data-recipe-ads-inserted', 'true')

    const dynamic = new Dynamic({
      slotFactory: this.slotFactory,
      slotClass: this.SLOT_CLASS,
      target,
      startingParagraphNumber: this.startingParagraphNumber,
      endingParagraphNumber: this.endingParagraphNumber,
      density: this.density,
      contentRequireText: this.contentRequireText,
      minParagraphBuffer: this.minParagraphBuffer,
      divBuster: this.divBuster,
      listBuster: this.listBuster,
      optShortFormContent: false,
      optimizeCLSMode: false
    })

    // dynamic.createSlots() is async but I don't think we need to await it here.
    // Nothing waits on RecipeSlots being rendered, unlike ContentSlots.
    dynamic.createSlots()

    return true
  }
}
